import React, { useCallback, useEffect, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { enUS } from "date-fns/locale";
import { toast } from "sonner";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "./bookingsCalendar.css";
import { api, formatApiError } from "@/lib/api";
import { Calendar as DayCal } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales: { "en-US": enUS } });
const DnDCalendar = withDragAndDrop(Calendar);

function RsvpLine({ bookingId }) {
  const [data, setData] = useState(null);
  const [cap, setCap] = useState("");
  const [waitlist, setWaitlist] = useState([]);
  useEffect(() => {
    api.get(`/site/session/${bookingId}`).then(({ data: d }) => {
      setData({ count: d.rsvp_count || 0, names: d.attendees || [] });
      setCap(d.capacity || "");
    }).catch(() => {});
    api.get(`/admin/site/booking/${bookingId}/waitlist`).then(({ data: w }) => setWaitlist(w || [])).catch(() => {});
  }, [bookingId]);
  const saveCap = async () => {
    try {
      const v = cap === "" ? null : Math.max(1, parseInt(cap, 10) || 1);
      await api.put(`/admin/site/booking/${bookingId}/capacity`, { capacity: v });
      toast.success(v ? `Max headcount set to ${v}` : "Headcount limit removed");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="mt-1">
      {data && data.count > 0 && (
        <div className="text-xs font-bold text-[#3F6212]" data-testid="calendar-rsvp-count">
          {data.count} {data.count === 1 ? "person has" : "people have"} tapped "I'm coming"
          {data.names.length > 0 && <span className="font-normal text-[#52525B]"> — {data.names.slice(0, 8).join(", ")}{data.count > data.names.length || data.names.length > 8 ? "…" : ""}</span>}
        </div>
      )}
      {waitlist.length > 0 && (
        <div className="text-xs mt-1" data-testid="calendar-waitlist">
          <span className="font-bold text-[#B45309]">{waitlist.length} on the waitlist</span>
          <span className="text-[#52525B]"> — {waitlist.slice(0, 8).map((w) =>
            `${w.name || "Someone"} (${w.phone}${w.notified ? ", texted" : ""})`).join(", ")}{waitlist.length > 8 ? "…" : ""}</span>
          <span className="block text-[11px] text-[#71717A]">Raise the max headcount to text the next in line automatically.</span>
        </div>
      )}
      <div className="flex items-center gap-1.5 mt-1">
        <span className="text-[11px] text-[#52525B]">Max headcount</span>
        <input type="number" min="1" value={cap} onChange={(e) => setCap(e.target.value)} placeholder="∞"
          style={{ width: 64 }} className="input-base text-xs py-0.5" data-testid="calendar-capacity-input" />
        <button className="btn-secondary text-[11px] py-0.5 px-2" onClick={saveCap} data-testid="calendar-capacity-save">Set</button>
      </div>
    </div>
  );
}

const styleFor = (ev, highlightId) => {
  let s;
  if (ev.source === "booking" && ev.status === "pending")
    s = { backgroundColor: "#FEF3C7", color: "#92400E", border: "1px solid #F59E0B" };
  else if (ev.source === "booking")
    s = { backgroundColor: "#DCFCE7", color: "#14532D", border: "1px solid #22C55E" };
  else if (ev.source === "block")
    s = { backgroundColor: "#FEE2E2", color: "#7F1D1D", border: "1px solid #F87171" };
  else
    s = { backgroundColor: "#E0E7FF", color: "#1E3A8A", border: "1px solid #818CF8" };
  if (highlightId && ev.id === highlightId)
    s = { ...s, boxShadow: "0 0 0 2px #0A0D14", fontWeight: 700 };
  return { style: { ...s, borderRadius: 4, fontSize: 12 } };
};

export const BookingsCalendar = ({ highlightId, initialDate, initialView, autoOpenHighlight = true, onChanged, onBlockRequest, refreshToken = 0 }) => {
  const [events, setEvents] = useState([]);
  const [gcalConnected, setGcalConnected] = useState(true);
  const [gcalError, setGcalError] = useState(null);
  const [loading, setLoading] = useState(false);
  // View preference is remembered (like Google Calendar); email deep-links default to Day.
  const [view, setViewState] = useState(() => {
    if (initialView) return initialView;
    try {
      const saved = localStorage.getItem("bookingsCalView");
      if (saved && ["month", "week", "day", "agenda"].includes(saved)) return saved;
    } catch { /* default below */ }
    return initialDate ? "day" : "week";
  });
  const setView = (v) => {
    setViewState(v);
    try { localStorage.setItem("bookingsCalView", v); } catch { /* non-blocking */ }
  };
  const [date, setDate] = useState(() => (initialDate ? new Date(`${initialDate}T12:00:00`) : new Date()));
  const [sel, setSel] = useState(null);
  const [resched, setResched] = useState(null); // { ev, date, time }
  const [dpOpen, setDpOpen] = useState(false);
  const [acting, setActing] = useState(false);
  const autoOpenedRef = React.useRef(false);

  // Deep link from the booking email: open the request straight away.
  useEffect(() => {
    if (!autoOpenHighlight || !highlightId || autoOpenedRef.current || !events.length) return;
    const ev = events.find((e) => e.source === "booking" && e.id === highlightId);
    if (ev) { autoOpenedRef.current = true; setSel(ev); }
  }, [highlightId, events]);

  const fetchRange = useCallback(async (d, v) => {
    const start = new Date(d);
    const end = new Date(d);
    if (v === "month") {
      start.setDate(1); start.setDate(start.getDate() - 7);
      end.setMonth(end.getMonth() + 1, 1); end.setDate(end.getDate() + 7);
    } else if (v === "week") {
      start.setDate(start.getDate() - start.getDay());
      end.setTime(start.getTime()); end.setDate(end.getDate() + 6);
    } else if (v === "agenda") {
      end.setDate(end.getDate() + 30);
    }
    const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/site/calendar-events?start=${iso(start)}&end=${iso(end)}`);
      setGcalConnected(!!data.gcal_connected);
      setGcalError(data.gcal_error || null);
      setEvents((data.events || []).map((e) => ({
        ...e,
        start: new Date(e.all_day && !String(e.start).includes("T") ? `${e.start}T00:00:00` : e.start),
        end: new Date(e.all_day && !String(e.end).includes("T") ? `${e.end}T00:00:00` : e.end),
        allDay: !!e.all_day,
      })));
    } catch { /* keep last events */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRange(date, view); }, [date, view, fetchRange, refreshToken]);

  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const hm = (d) => `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  const handleSelectSlot = (si) => {
    if (!onBlockRequest) return;
    if (view === "month") {
      const days = si.slots && si.slots.length ? si.slots : [si.start];
      onBlockRequest({ date: iso(days[0]), end_date: iso(days[days.length - 1]) });
    } else {
      onBlockRequest({ date: iso(si.start), end_date: iso(si.start), start_time: hm(si.start), end_time: hm(si.end) });
    }
  };

  const act = async (status) => {
    setActing(true);
    try {
      await api.patch(`/admin/site/bookings/${sel.id}`, { status });
      toast.success(status === "confirmed" ? "Booking confirmed — the client gets their confirmation email" : `Booking ${status}`);
      setSel(null);
      fetchRange(date, view);
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setActing(false); }
  };

  const onEventDrop = ({ event, start }) => {
    if (event.source !== "booking" || ["declined", "cancelled"].includes(event.status)) return;
    const newTime = view === "month" ? event.time : hm(start);
    setResched({ ev: event, date: iso(start), time: newTime });
  };

  const doReschedule = async () => {
    setActing(true);
    try {
      const { data } = await api.patch(`/admin/site/bookings/${resched.ev.id}/reschedule`,
        { date: resched.date, time: resched.time });
      toast.success(`Rescheduled${data.notified?.length ? ` — client notified by ${data.notified.join(" + ")}` : " — no contact info to notify"}`);
      setResched(null);
      fetchRange(date, view);
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setActing(false); }
  };

  return (
    <div data-testid="bookings-calendar">
      <div className="flex flex-wrap items-center gap-3 mb-2 text-[11px]">
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{ background: "#FEF3C7", border: "1px solid #F59E0B" }} /> Pending request</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{ background: "#DCFCE7", border: "1px solid #22C55E" }} /> Confirmed session</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{ background: "#FEE2E2", border: "1px solid #F87171" }} /> Blocked / time off</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{ background: "#E0E7FF", border: "1px solid #818CF8" }} /> Google Calendar</span>
        <label className="flex items-center gap-1.5 ml-auto text-xs font-semibold text-[#000000]">
          Jump to
          <input type="date" className="input-base text-xs" style={{ padding: "5px 6px", width: 132 }}
            data-testid="calendar-jump-date"
            onChange={(e) => { if (e.target.value) setDate(new Date(`${e.target.value}T12:00:00`)); }} />
        </label>
        {loading && <span className="text-[#000000]">refreshing…</span>}
      </div>
      {!gcalConnected && (
        <div className="text-xs rounded-lg border border-[#FBBF24] bg-[#FEF9C3] text-[#854D0E] px-3 py-2 mb-2" data-testid="calendar-gcal-warning">
          Google Calendar isn't connected — showing app bookings only. Connect it in the "Booking Calendar" card below to see everything you've blocked off.
        </div>
      )}
      {gcalConnected && gcalError && (
        <div className="text-xs rounded-lg border border-[#FBBF24] bg-[#FEF9C3] text-[#854D0E] px-3 py-2 mb-2" data-testid="calendar-gcal-error">
          Google Calendar is connected but returned an error while loading your events: "{gcalError}".
          If this mentions permissions, reconnect and make sure the calendar checkbox is ticked on the Google consent screen.
        </div>
      )}
      <DnDCalendar
        localizer={localizer}
        events={events}
        views={["month", "week", "day", "agenda"]}
        view={view}
        onView={setView}
        date={date}
        onNavigate={setDate}
        step={30}
        timeslots={2}
        popup
        selectable={!!onBlockRequest}
        onSelectSlot={handleSelectSlot}
        style={{ height: 640 }}
        eventPropGetter={(ev) => styleFor(ev, highlightId)}
        onSelectEvent={(ev) => { if (ev.source === "booking" || ev.source === "block") setSel(ev); }}
        onEventDrop={onEventDrop}
        draggableAccessor={(ev) => ev.source === "booking" && !["declined", "cancelled"].includes(ev.status)}
        resizable={false}
        min={new Date(1970, 0, 1, 6, 0)}
        max={new Date(1970, 0, 1, 22, 0)}
        tooltipAccessor={(ev) => ev.title}
      />
      {sel && sel.source === "block" && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="calendar-block-modal" onClick={() => setSel(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-[#FEE2E2] text-[#7F1D1D]">Blocked time</span>
            <div className="font-bold text-[#000000] mt-1">{sel.label || "Time off"}</div>
            <div className="text-sm text-[#000000] mb-1">
              {sel.date}{sel.end_date && sel.end_date !== sel.date ? ` → ${sel.end_date}` : ""}{sel.allDay ? " (all day)" : ""}
            </div>
            <div className="text-xs text-[#000000]">Clients can't book during this — it's enforced on the public booking page.</div>
            <div className="flex justify-end gap-2 mt-4">
              <button className="btn-ghost text-xs" onClick={() => setSel(null)}>Close</button>
              <button className="btn-secondary text-xs" disabled={acting} data-testid="calendar-unblock-btn"
                onClick={async () => {
                  setActing(true);
                  try {
                    await api.delete(`/admin/site/booking/blocks/${sel.id}`);
                    toast.success("Block removed — those days are open again");
                    setSel(null); fetchRange(date, view); onChanged && onChanged();
                  } catch (e) { toast.error(formatApiError(e)); }
                  finally { setActing(false); }
                }}>Remove this block</button>
            </div>
          </div>
        </div>
      )}
      {sel && sel.source === "booking" && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="calendar-booking-modal" onClick={() => setSel(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${sel.status === "pending" ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#DCFCE7] text-[#14532D]"}`}>{sel.status}</span>
              <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${sel.session_type === "virtual" ? "bg-[#DBEAFE] text-[#1D4ED8]" : "bg-[#ECFCCB] text-[#3F6212]"}`}>{sel.session_type === "virtual" ? "Virtual" : "In person"}</span>
            </div>
            <div className="font-bold text-[#000000]">{sel.name}</div>
            <div className="text-sm text-[#000000] mb-1">{sel.date} at {sel.time}</div>
            <div className="text-xs text-[#000000]">{sel.phone}{sel.email ? ` · ${sel.email}` : ""}</div>
            {sel.note && <div className="text-xs text-[#000000] italic mt-1">“{sel.note}”</div>}
            <RsvpLine bookingId={sel.id} />
            <div className="flex justify-end gap-2 mt-4 flex-wrap">
              <button className="btn-ghost text-xs" onClick={() => setSel(null)}>Close</button>
              <button className="btn-secondary text-xs" data-testid="calendar-share-btn"
                onClick={async () => {
                  try { await navigator.clipboard.writeText(`${window.location.origin}/session/${sel.id}`); toast.success("Session page link copied — share it with the group"); } catch { /* ignore */ }
                }}>Copy share link</button>
              <button className="btn-secondary text-xs" data-testid="calendar-poster-btn"
                onClick={() => window.open(`/session/${sel.id}/poster`, "_blank", "noopener")}>Print poster</button>
              <button className="btn-secondary text-xs" data-testid="calendar-reschedule-btn"
                onClick={() => { setResched({ ev: sel, date: sel.date, time: sel.time }); setSel(null); }}>Reschedule</button>
              {sel.status === "pending" ? (
                <>
                  <button className="btn-secondary text-xs" disabled={acting} onClick={() => act("declined")} data-testid="calendar-decline-btn">Decline</button>
                  <button className="btn-primary text-xs" disabled={acting} onClick={() => act("confirmed")} data-testid="calendar-confirm-btn">{acting ? "Working…" : "Confirm"}</button>
                </>
              ) : (
                <button className="btn-secondary text-xs" disabled={acting} onClick={() => act("cancelled")} data-testid="calendar-cancel-btn">Cancel booking</button>
              )}
            </div>
          </div>
        </div>
      )}
      {resched && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="calendar-reschedule-modal" onClick={() => setResched(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <div className="font-bold text-[#000000] mb-1">Reschedule {resched.ev.name}</div>
            <div className="text-xs text-[#000000] mb-3">
              Currently {resched.ev.date} at {resched.ev.time}.{" "}
              {resched.ev.email || resched.ev.phone
                ? `They'll be notified by ${[resched.ev.email && "email", resched.ev.phone && "text"].filter(Boolean).join(" and ")}.`
                : "No contact info on file — nobody will be notified."}
            </div>
            <div className="flex gap-2 mb-4">
              <label className="text-xs font-semibold text-[#000000] flex-1">New date
                <Popover open={dpOpen} onOpenChange={setDpOpen}>
                  <PopoverTrigger asChild>
                    <button type="button" className="input-base mt-1 w-full text-sm text-left cursor-pointer" data-testid="reschedule-date">
                      {resched.date || "Pick a date"}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-auto z-[70]" align="start">
                    <DayCal mode="single"
                      selected={resched.date ? new Date(`${resched.date}T12:00:00`) : undefined}
                      defaultMonth={resched.date ? new Date(`${resched.date}T12:00:00`) : new Date()}
                      onSelect={(d) => { if (d) { setResched((r) => ({ ...r, date: iso(d) })); setDpOpen(false); } }} />
                  </PopoverContent>
                </Popover>
              </label>
              <label className="text-xs font-semibold text-[#000000] flex-1">New time
                <input type="time" className="input-base mt-1 w-full text-sm" value={resched.time}
                  onChange={(e) => setResched({ ...resched, time: e.target.value })} data-testid="reschedule-time" />
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <button className="btn-ghost text-xs" onClick={() => setResched(null)}>Cancel</button>
              <button className="btn-primary text-xs" disabled={acting || !resched.date || !resched.time}
                onClick={doReschedule} data-testid="reschedule-save-btn">{acting ? "Working…" : "Reschedule & notify"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
